import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import * as Yup from 'yup';

import Table from '../../../../components/Table';
import CrudForm from '../../../../components/Form/CrudForm';

import BrokerTeamService from '../../../../services/BrokerTeamService';
import { useUiStore } from '../../../../store';
import useUserData from '../../../../hooks/useUserData';
import BrokerService from '../../../../services/BrokerService';

const cols = [
  {
    title: 'Name',
    key: 'name',
    type: 'user',
  },
  {
    title: 'Role',
    key: 'role',
    type: 'userRole',
  },
  {
    title: 'Email',
    key: 'email',
  },
  {
    title: 'Enabled',
    key: 'isEnabled',
    format: (value) => (value ? 'Yes' : 'No'),
  },
];

export default function TeamUsersPage() {
  const { id } = useParams();

  const showDialogModal = useUiStore((state) => state.showDialogModal);

  const { user } = useUserData();

  const { data: users } = useQuery({
    queryKey: ['users', user?.brokerId],
    queryFn: () => BrokerService.getBrokerUsers({ id: user?.brokerId }),
    enabled: !!user?.brokerId,
  });

  const { data: brokerTeamUsers, refetch } = useQuery({
    queryKey: ['brokerTeamUsers', id],
    queryFn: () => BrokerTeamService.getBrokerTeamUsers(id),
    enabled: !!id,
  });

  const { mutate: addUserToBrokerTeam, isPending: isAddingUser } = useMutation({
    mutationFn: (formValues) =>
      BrokerTeamService.addUserToBrokerTeam({ id, userId: formValues.userId }),
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: removeUserFromBrokerTeam, isPending: isRemovingUser } =
    useMutation({
      mutationFn: (row) =>
        BrokerTeamService.removeUserFromBrokerTeam({ id, userId: row.id }),
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const fields = useMemo(
    () => [
      {
        label: 'Add User to Team',
        placeholder: 'Select a user',
        name: 'userId',
        type: 'select',
        validation: Yup.string().required('This field is required'),
        options: users
          ?.map((user) => ({
            label: user.name,
            value: user.id,
          }))
          ?.filter(
            (user) =>
              !brokerTeamUsers?.some((teamUser) => teamUser.id === user.value)
          ),
      },
    ],
    [brokerTeamUsers, users]
  );

  const actions = [
    {
      title: 'Remove',
      icon: 'delete',
      danger: true,
      onClick: (row) => {
        showDialogModal({
          title: 'Remove User from Team',
          text: `Are you sure you want to remove ${row.name} from this team?`,
          onConfirm: () => {
            removeUserFromBrokerTeam(row);
          },
        });
      },
    },
  ];

  return (
    <div>
      <div className="mb-5">
        <CrudForm
          fields={fields}
          submitButtonLabel="Submit"
          isPending={isRemovingUser || isAddingUser}
          createHandler={addUserToBrokerTeam}
        />
      </div>
      <Table
        countName="Users"
        cols={cols}
        rows={brokerTeamUsers}
        actions={actions}
      />
    </div>
  );
}
